import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { DocLocation } from '../../../../types';
import { getDriveHierarchy } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { Dispatch, SetStateAction } from 'react';
import DriveLocationBreadcrumb from './drive-location-breadcrumb';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import AddToDrive from '@mui/icons-material/AddToDrive';
import PeopleOutlineOutlined from '@mui/icons-material/PeopleOutlineOutlined';

export function generateDriveUrl(
  mimeType: string,
  id: string,
  loggedInUser: string,
  primaryDomain: string
) {
  if (id == 'shared-with-me') {
    return `https://drive.google.com/drive/${id}`;
  }

  if (mimeType == null) {
    return '';
  }

  if (mimeType.includes('folder')) {
    return `https://drive.google.com/drive/folders/${id}`;
  }

  if (mimeType.includes('application/vnd.google-apps.spreadsheet')) {
    const domain = loggedInUser.split('@')[1];
    return `https://docs.google.com/a/${domain}/spreadsheet/ccc?key=${id}#gid=0`;
  }

  return `https://drive.google.com/a/${primaryDomain}/file/d/${id}/view`;
}

interface DriveSearchLocationProps {
  params: GridRenderCellParams;
  loggedInUser: string;
  primaryDomain: string;
  hierarchy: DocLocation[] | null;
  setHierarchy: Dispatch<SetStateAction<DocLocation[] | null>>;
  hierarchyCache: Record<string, DocLocation[]>;
  setHierarchyCache: Dispatch<SetStateAction<Record<string, DocLocation[]>>>;
  hoveredRowId: string | null;
  setHoveredRowId: Dispatch<SetStateAction<string | null>>;
  isHierarchyLoading: boolean;
  setIsHierarchyLoading: Dispatch<SetStateAction<boolean>>;
}

export default function DriveSearchLocation({
  params,
  loggedInUser,
  primaryDomain,
  hierarchy,
  setHierarchy,
  hierarchyCache,
  setHierarchyCache,
  hoveredRowId,
  setHoveredRowId,
  isHierarchyLoading,
  setIsHierarchyLoading,
}: DriveSearchLocationProps) {
  const { location } = params.row;
  if (!location) {
    return null;
  }

  const isLocationUnknown = location.name === 'Location Unknown';
  const hasAccess = location.hasAccess == 'true';

  const isSharedWithMe = !!location.isSharedWithMe;
  const isSharedDrive = !!location.isSharedDrive;
  const iconSx = { width: '15px', height: '15px' };
  const displayIcon = isSharedWithMe ? (
    <PeopleOutlineOutlined sx={iconSx} />
  ) : isSharedDrive || location.name == 'My Drive' ? (
    <AddToDrive sx={iconSx} />
  ) : (
    <Box
      component="img"
      src={location.iconLink}
      sx={iconSx}
      alt={location.name}
    />
  );

  const url = generateDriveUrl(
    location.mimeType,
    location.id,
    loggedInUser,
    primaryDomain
  );

  const handleMouseEnter = async () => {
    if (
      !hasAccess ||
      hoveredRowId === params.id ||
      location.isSharedWithMe === 'true' ||
      location.isSharedDrive === 'true' ||
      location.name === 'My Drive'
    ) {
      return;
    }
    setHoveredRowId(params.id as string);

    if (hierarchyCache[location.id]) {
      setHierarchy(hierarchyCache[location.id]);
      return;
    }

    setIsHierarchyLoading(true);
    setHierarchy(null);
    try {
      const hierarchyData = await getDriveHierarchy(
        location.id,
        location.docOwner
      );
      setHierarchy(hierarchyData);
      setHierarchyCache((prev) => ({
        ...prev,
        [location.id]: hierarchyData,
      }));
    } catch (error) {
      handleErrors({
        error,
        snackbarMessage: 'Could not load folder path.',
      });
      setHierarchy([]);
      setHierarchyCache((prev) => ({ ...prev, [location.id]: [] }));
    } finally {
      setIsHierarchyLoading(false);
    }
  };

  const handleMouseLeave = () => {
    setHoveredRowId(null);
    setHierarchy(null);
  };

  const isHovered = hoveredRowId === params.id;
  const hasHierarchy = hierarchy != null && hierarchy.length > 0;

  const locationContent = (
    <Stack
      component={hasAccess ? Link : Box}
      href={hasAccess ? url : undefined}
      target={hasAccess ? '_blank' : undefined}
      direction="row"
      alignItems="center"
      gap={1}
      id="location"
      sx={{
        overflow: 'hidden',
        textDecoration: 'none',
        color:
          hasAccess || isLocationUnknown ? 'text.primary' : 'text.disabled',
        width: '100%',
        height: '100%',
      }}
    >
      {!isLocationUnknown && location.name !== '-' && displayIcon}
      <Typography variant="body2" noWrap={true}>
        {location.name}
      </Typography>
    </Stack>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        ...(hasAccess && {
          '&:hover #location': {
            backgroundColor: 'action.hover',
            height: 'calc(100% - 10px)',
            padding: '0 8px',
          },
          '&:hover .column-overlay': {
            opacity: 1,
            visibility: 'visible',
          },
        }),
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!isLocationUnknown ? (
        <Tooltip
          title={
            hasAccess
              ? location.name
              : `You don't have access on ${location.name}`
          }
        >
          {locationContent}
        </Tooltip>
      ) : (
        locationContent
      )}
      {isHovered && (isHierarchyLoading || hasHierarchy) ? (
        <Stack
          className="column-overlay"
          direction="row"
          alignItems="center"
          gap={1}
          sx={(theme) => ({
            position: 'absolute',
            right: 0,
            minWidth: '100%',
            width: 'auto',
            height: 'calc(100% - 10px)',
            backgroundColor:
              theme.palette.mode === 'dark' ? 'primary.main' : 'primary.light',
            padding: '0 8px',
            visibility: 'hidden',
            borderRadius: '2px',
            boxShadow: theme.shadows[1],
            color: 'text.primary',
          })}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              whiteSpace: 'nowrap',
              width: '100%',
              maxWidth: '600px',
              overflowX: 'auto',
            }}
          >
            {isHierarchyLoading ? (
              <Skeleton
                variant="text"
                sx={(theme) => ({
                  width: '80%',
                  fontSize: '1rem',
                  ...(theme.palette.mode === 'dark' && {
                    backgroundColor: 'primary.light',
                  }),
                })}
              />
            ) : (
              <Breadcrumbs
                separator="›"
                aria-label="breadcrumb"
                maxItems={4}
                sx={{
                  '& .MuiButtonBase-root': {
                    color: 'inherit',
                    backgroundColor: 'transparent',
                  },
                  '& .MuiBreadcrumbs-ol': {
                    flexWrap: 'nowrap',
                  },
                  '& .MuiBreadcrumbs-li': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                {hierarchy?.map((doc) => (
                  <DriveLocationBreadcrumb
                    key={doc.id}
                    doc={doc}
                    loggedInUser={loggedInUser}
                    primaryDomain={primaryDomain}
                  />
                ))}
                <DriveLocationBreadcrumb
                  doc={location}
                  loggedInUser={loggedInUser}
                  primaryDomain={primaryDomain}
                  isCurrent={true}
                />
              </Breadcrumbs>
            )}
          </Stack>
        </Stack>
      ) : null}
    </Box>
  );
}
